// BusinessDashboard.js

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Chip } from '@mui/material';
import {
  Grid,
  Alert,
  MenuItem,
  Box,
  Typography,
  TextField,
  Popover,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CropRoundedIcon from '@mui/icons-material/CropRounded';
import UnsubscribeOutlinedIcon from '@mui/icons-material/UnsubscribeOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { AppProvider } from '@toolpad/core/AppProvider';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { accountService } from '../../services/api/accountService';
import { businessService } from '../../services/api/businessService';
import { navigateToStorefront } from '../../utils/storefrontPath';
import { messagingService } from '../../services/api/messagingService';
import { getBusinessAnalytics } from '../../services/api/analyticsService';
import { getTradeDisplayName } from '../../constants/mockData/allTrades';
import { getSubserviceDisplayLabel } from '../../utils/getSubserviceDisplayLabel';
import { appTheme } from '../../theme/appTheme';
import NkySnackbarAlert from '../common/NkySnackbarAlert/NkySnackbarAlert';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import DashboardNavigationBar from '../DashboardNavigationBar/DashboardNavigationBar';
import StorefrontOfflineAlert from '../StorefrontOfflineAlert/StorefrontOfflineAlert';
import BusinessLogoCropModal from '../BusinessLogoCropModal/BusinessLogoCropModal';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  DashboardContainer,
  SidebarContainer,
  ContentWrapper,
  BusinessHeaderBox,
  BusinessHeading,
  WelcomeText,
  InfoPaper,
  FormNotice,
  SaveButton,
  CancelButton,
  StyledTextField,
  InfoBlock,
  InfoBlockIconWrap,
  InfoBlockText,
  InfoChip,
  PopoverContent,
  PopoverTitle,
  PopoverBody,
  StorefrontButton,
  PhotoUploadContainer,
  PhotoContainer,
  PhotoPlaceholder,
  PhotoUploadButton,
  PhotoError,
  Label,
  Value,
  LabeledInputWrapper,
  LabelWithIcon,
  EditButton,
  BusinessInfoTitle,
  // Analytics styled components
  AnalyticsSection,
  AnalyticsHeader,
  AnalyticsTitle,
  KpiRow,
  KpiCard,
  KpiIconWrap,
  KpiValue,
  KpiLabel,
  ServiceBreakdownCard,
  ServiceBreakdownTitle,
  ServiceGroupRow,
  ServiceName,
  ServiceCount,
  AnalyticsEmptyState,
} from './BusinessDashboard.styled';

import EditIcon from '@mui/icons-material/Edit';
import { formatPhoneInputMask, phoneDigitsForApi } from '../../utils/phoneInputMask';
import { SERVICE_AREA_STATES } from '../../utils/serviceAreaStates';
import {
  normalizeBusinessAddress,
  normalizeBusinessEmail,
  normalizeBusinessFormFieldValue,
  normalizeBusinessWebsiteUrl,
} from '../../utils/normalizeBusinessAddress';
import { fetchImageAsBlobUrl } from '../../utils/fetchImageAsBlobUrl';

function logoUploadBaseFromFileName(fileName) {
  if (!fileName || typeof fileName !== 'string') return 'business-logo';
  const stem = fileName.replace(/\.[^.]+$/, '').trim();
  return stem || 'business-logo';
}

function logoUploadBaseFromPhotoUrl(url) {
  if (!url || typeof url !== 'string') return 'business-logo';
  const part = url.split('/').pop()?.split('?')[0] || '';
  try {
    const decoded = decodeURIComponent(part);
    return decoded.replace(/\.[^.]+$/, '').trim() || 'business-logo';
  } catch {
    return part.replace(/\.[^.]+$/, '').trim() || 'business-logo';
  }
}

function withFormattedBusinessPhone(business) {
  if (!business || typeof business !== 'object') return business;
  return {
    ...business,
    BusinessPhoneNumber: formatPhoneInputMask(business.BusinessPhoneNumber || ''),
  };
}

/** Group byService rows by service for hierarchical display (subservice-level search counts). */
function groupByServiceForDisplay(byService, enrolledServices) {
  if (!byService?.length) return [];
  const groups = new Map();
  for (const row of byService) {
    const key = row.serviceShortName;
    if (!groups.has(key)) {
      const serviceLabel = getTradeDisplayName(key) || key || '—';
      groups.set(key, {
        serviceShortName: key,
        serviceLabel,
        serviceOnlyCount: undefined,
        subservices: [],
      });
    }
    const g = groups.get(key);
    if (row.subserviceShortName) {
      const count = row.count || 0;
      if (count <= 0) continue;
      const subLabel =
        getSubserviceDisplayLabel(row.subserviceShortName, {
          serviceType: key,
          enrolledServices,
        }) || row.subserviceShortName;
      g.subservices.push({ subLabel, count, subserviceShortName: row.subserviceShortName });
    } else {
      g.serviceOnlyCount = row.count;
    }
  }

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      subservices: [...group.subservices].sort((a, b) => b.count - a.count),
    }))
    .filter(
      (group) =>
        (group.serviceOnlyCount || 0) > 0 || group.subservices.length > 0,
    )
    .sort((a, b) => {
      const aTotal =
        (a.serviceOnlyCount || 0) +
        a.subservices.reduce((sum, sub) => sum + sub.count, 0);
      const bTotal =
        (b.serviceOnlyCount || 0) +
        b.subservices.reduce((sum, sub) => sum + sub.count, 0);
      return bTotal - aTotal || a.serviceLabel.localeCompare(b.serviceLabel);
    });
}

/** Total app-wide service searches attributed to this business (trade-level rows avoid double-count). */
function getTotalServiceSearches(byService) {
  if (!byService?.length) return 0;
  return byService
    .filter((r) => !r.subserviceShortName)
    .reduce((sum, r) => sum + (r.count || 0), 0);
}

/** Trade with the most attributed searches in the selected period. */
function getTopService(groups) {
  if (!groups.length) return '—';
  const sorted = [...groups].sort(
    (a, b) => (b.serviceOnlyCount || 0) - (a.serviceOnlyCount || 0),
  );
  return sorted[0]?.serviceLabel || '—';
}

/** Top subservice label across all trades (for AI / wizard sub-level events). */
function getTopSubservice(groups) {
  let top = null;
  for (const group of groups) {
    for (const sub of group.subservices) {
      if (!top || sub.count > top.count) {
        top = { ...sub, serviceLabel: group.serviceLabel };
      }
    }
  }
  if (!top) return null;
  return top.subLabel;
}

function groupInquiriesByServiceForDisplay(inquiryByService, enrolledServices) {
  if (!inquiryByService?.length) return [];
  const groups = new Map();
  for (const row of inquiryByService) {
    const key = row.serviceShortName;
    if (!groups.has(key)) {
      groups.set(key, {
        serviceShortName: key,
        serviceLabel: getTradeDisplayName(key) || key,
        subservices: [],
      });
    }
    const subLabel =
      getSubserviceDisplayLabel(row.subserviceShortName, {
        serviceType: row.serviceShortName,
        enrolledServices,
      }) ||
      getTradeDisplayName(row.serviceShortName) ||
      row.serviceShortName;
    groups.get(key).subservices.push({
      subLabel,
      count: row.count,
      subserviceShortName: row.subserviceShortName,
    });
  }
  return Array.from(groups.values())
    .map((g) => ({
      ...g,
      subservices: [...g.subservices].sort((a, b) => b.count - a.count),
    }))
    .sort((a, b) => {
      const aTotal = a.subservices.reduce((s, sub) => s + sub.count, 0);
      const bTotal = b.subservices.reduce((s, sub) => s + sub.count, 0);
      return bTotal - aTotal || a.serviceLabel.localeCompare(b.serviceLabel);
    });
}

/* ─────────────────────────────────────────────
   Analytics Panel – extracted for clarity
   ───────────────────────────────────────────── */
function AnalyticsPanel({
  analytics,
  analyticsLoading,
  analyticsError,
  analyticsFrom,
  analyticsTo,
  enrolledServices,
  onFromChange,
  onToChange,
}) {
  const datePickers = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mb: 2.5,
        flexWrap: 'wrap',
      }}
    >
      <CalendarTodayOutlinedIcon
        sx={{ fontSize: '1rem', color: '#2d46b9' }}
      />
      <TextField
        type="date"
        size="small"
        label="From"
        value={analyticsFrom}
        onChange={(e) => onFromChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{
          width: 160,
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            fontSize: '0.85rem',
            fontWeight: 600,
          },
        }}
      />
      <Typography sx={{ color: '#8893a4', fontWeight: 600, fontSize: '0.85rem' }}>
        to
      </Typography>
      <TextField
        type="date"
        size="small"
        label="To"
        value={analyticsTo}
        onChange={(e) => onToChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{
          width: 160,
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            fontSize: '0.85rem',
            fontWeight: 600,
          },
        }}
      />
    </Box>
  );

  if (analyticsLoading) {
    return (
      <AnalyticsSection>
        <AnalyticsHeader>
          <AnalyticsTitle>Your Performance at a Glance</AnalyticsTitle>
        </AnalyticsHeader>
        {datePickers}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <NkySpinner />
        </Box>
      </AnalyticsSection>
    );
  }

  if (analyticsError) {
    return (
      <AnalyticsSection>
        <AnalyticsHeader>
          <AnalyticsTitle>Your Performance at a Glance</AnalyticsTitle>
        </AnalyticsHeader>
        {datePickers}
        <Alert severity="info" sx={{ mt: 1, borderRadius: '12px' }}>
          {analyticsError === 'Forbidden' ? (
            <>
              Analytics access is not enabled for your business.{' '}
              <a
                href="mailto:lukewilliams@nkyconnect.com,chandlerfarmer@nkyconnect.com"
                style={{
                  color: 'inherit',
                  fontWeight: 600,
                  textDecoration: 'underline',
                }}
              >
                Contact us
              </a>{' '}
              to upgrade.
            </>
          ) : (
            analyticsError
          )}
        </Alert>
      </AnalyticsSection>
    );
  }

  if (!analytics) return null;

  const groups = groupByServiceForDisplay(analytics.byService, enrolledServices);
  const totalServiceSearches = getTotalServiceSearches(analytics.byService);
  const topService = getTopService(groups);
  const topSubservice = getTopSubservice(groups);
  const maxCount = Math.max(
    ...groups.map((g) => g.serviceOnlyCount || 0),
    ...groups.flatMap((g) => g.subservices.map((s) => s.count)),
    1,
  );

  const inquiries = analytics.inquiries ?? {
    totalInquiries: 0,
    byService: [],
  };
  const contactLeads = analytics.contactLeads ?? {
    phoneClicks: 0,
    emailClicks: 0,
    total: 0,
  };
  const totalLeads =
    (inquiries.totalInquiries ?? 0) + (contactLeads.total ?? 0);
  const inquiryGroups = groupInquiriesByServiceForDisplay(
    inquiries.byService,
    enrolledServices,
  );
  const maxInquiryCount = Math.max(
    ...inquiryGroups.flatMap((g) => g.subservices.map((s) => s.count)),
    1,
  );

  return (
    <AnalyticsSection>
      <AnalyticsHeader>
        <AnalyticsTitle>Your Performance at a Glance</AnalyticsTitle>
      </AnalyticsHeader>

      {datePickers}

      {/* ── KPI cards ── */}
      <KpiRow>
        <KpiCard elevation={0} delay="0s" accent="linear-gradient(90deg, #01ebb2, #0bbfa0)">
          <KpiIconWrap bg="#e8faf5" iconColor="#01c99b">
            <VisibilityOutlinedIcon />
          </KpiIconWrap>
          <KpiValue>{analytics.storefrontViews ?? 0}</KpiValue>
          <KpiLabel>Storefront Views</KpiLabel>
        </KpiCard>

        <KpiCard elevation={0} delay="0.07s" accent="linear-gradient(90deg, #0d4efe, #6b8cff)">
          <KpiIconWrap bg="#eef2ff" iconColor="#4361ee">
            <CategoryOutlinedIcon />
          </KpiIconWrap>
          <KpiValue>{totalServiceSearches}</KpiValue>
          <KpiLabel>Service Searches</KpiLabel>
        </KpiCard>

        <KpiCard elevation={0} delay="0.14s" accent="linear-gradient(90deg, #f59e0b, #fbbf24)">
          <KpiIconWrap bg="#fef9ee" iconColor="#d97706">
            <TrendingUpIcon />
          </KpiIconWrap>
          <KpiValue style={{ fontSize: '1.35rem', fontWeight: 700 }}>
            {topSubservice || topService}
          </KpiValue>
          <KpiLabel>{topSubservice ? 'Top Subservice' : 'Top Service'}</KpiLabel>
        </KpiCard>

        <KpiCard elevation={0} delay="0.21s" accent="linear-gradient(90deg, #7c3aed, #a78bfa)">
          <KpiIconWrap bg="#f3efff" iconColor="#7c3aed">
            <MailOutlineIcon />
          </KpiIconWrap>
          <KpiValue>{totalLeads}</KpiValue>
          <KpiLabel>Leads</KpiLabel>
        </KpiCard>
      </KpiRow>

      {/* ── Service breakdown ── */}
      {groups.length > 0 && (
        <ServiceBreakdownCard elevation={0}>
          <ServiceBreakdownTitle>
            <BarChartRoundedIcon sx={{ color: '#0d4efe', fontSize: '1.2rem' }} />
            Searches by Service
          </ServiceBreakdownTitle>

          {groups.map((group) => {
            const parentCount = group.serviceOnlyCount || 0;
            const pct = Math.max((parentCount / maxCount) * 100, parentCount > 0 ? 4 : 0);
            return (
              <React.Fragment key={group.serviceShortName}>
                {/* Parent service */}
                <ServiceGroupRow isParent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <ServiceName>{group.serviceLabel}</ServiceName>
                    {parentCount > 0 && (
                      <Box
                        sx={{
                          flex: 1,
                          maxWidth: 200,
                          height: 10,
                          borderRadius: 5,
                          bgcolor: '#e0e6f0',
                          overflow: 'hidden',
                          ml: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: `${pct}%`,
                            height: '100%',
                            borderRadius: 5,
                            background:
                              'linear-gradient(90deg, #01ebb2, #0d4efe)',
                            transition: 'width 0.8s ease',
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                  <ServiceCount>{parentCount > 0 ? parentCount : '—'}</ServiceCount>
                </ServiceGroupRow>

                {/* Subservices */}
                {group.subservices.map((sub) => {
                  const subPct = Math.max((sub.count / maxCount) * 100, 4);
                  return (
                  <ServiceGroupRow
                    key={`${group.serviceShortName}-${sub.subserviceShortName}`}
                    isParent={false}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, pl: 2 }}>
                      <ServiceName isChild>
                        <FiberManualRecordIcon
                          sx={{ fontSize: '0.4rem', color: '#b0bec5' }}
                        />
                        {sub.subLabel}
                      </ServiceName>
                      <Box
                        sx={{
                          flex: 1,
                          maxWidth: 160,
                          height: 8,
                          borderRadius: 4,
                          bgcolor: '#e8ecf4',
                          overflow: 'hidden',
                          ml: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: `${subPct}%`,
                            height: '100%',
                            borderRadius: 4,
                            background:
                              'linear-gradient(90deg, rgba(1,235,178,0.85), rgba(13,78,254,0.85))',
                            transition: 'width 0.8s ease',
                          }}
                        />
                      </Box>
                    </Box>
                    <ServiceCount isChild>{sub.count}</ServiceCount>
                  </ServiceGroupRow>
                  );
                })}
              </React.Fragment>
            );
          })}
        </ServiceBreakdownCard>
      )}

      {!groups.length && (
        <AnalyticsEmptyState>
          <BarChartRoundedIcon sx={{ fontSize: 40, opacity: 0.4 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            No service search data available yet
          </Typography>
        </AnalyticsEmptyState>
      )}

      {/* ── Leads: inquiries plus storefront contact clicks ── */}
      <ServiceBreakdownCard elevation={0} sx={{ mt: 3 }}>
        <ServiceBreakdownTitle>
          <MailOutlineIcon sx={{ color: '#7c3aed', fontSize: '1.2rem' }} />
          Leads
        </ServiceBreakdownTitle>
        <Typography variant="body2" sx={{ color: '#5a6478', mb: 2, mt: -1 }}>
          Service inquiries plus clicks on your storefront phone and email.
        </Typography>

        {totalLeads > 0 ? (
          <>
            <ServiceGroupRow isParent>
              <ServiceName>Service inquiries</ServiceName>
              <ServiceCount>{inquiries.totalInquiries ?? 0}</ServiceCount>
            </ServiceGroupRow>
            <ServiceGroupRow isParent>
              <ServiceName>
                <PhoneOutlinedIcon sx={{ fontSize: '1.05rem', color: '#0d4efe' }} />
                Phone link clicks
              </ServiceName>
              <ServiceCount>{contactLeads.phoneClicks ?? 0}</ServiceCount>
            </ServiceGroupRow>
            <ServiceGroupRow isParent>
              <ServiceName>
                <EmailOutlinedIcon sx={{ fontSize: '1.05rem', color: '#0d4efe' }} />
                Email link clicks
              </ServiceName>
              <ServiceCount>{contactLeads.emailClicks ?? 0}</ServiceCount>
            </ServiceGroupRow>
            {inquiryGroups.length > 0 && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1.5, mt: 2 }}
                >
                  Inquiries by service
                </Typography>
                {inquiryGroups.map((group) => (
                  <Box key={`inq-${group.serviceShortName}`} sx={{ mb: 1.5 }}>
                    <ServiceGroupRow isParent>
                      <ServiceName>{group.serviceLabel}</ServiceName>
                    </ServiceGroupRow>
                    {group.subservices.map((sub) => {
                      const subPct = Math.max((sub.count / maxInquiryCount) * 100, 4);
                      return (
                        <ServiceGroupRow
                          key={`inq-${group.serviceShortName}-${sub.subserviceShortName || sub.subLabel}`}
                          isParent={false}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flex: 1,
                              pl: 2,
                            }}
                          >
                            <ServiceName isChild>
                              <FiberManualRecordIcon
                                sx={{ fontSize: '0.4rem', color: '#b0bec5' }}
                              />
                              {sub.subLabel}
                            </ServiceName>
                            <Box
                              sx={{
                                flex: 1,
                                maxWidth: 140,
                                height: 8,
                                borderRadius: 4,
                                bgcolor: '#e8ecf4',
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${subPct}%`,
                                  height: '100%',
                                  borderRadius: 4,
                                  background:
                                    'linear-gradient(90deg, rgba(124,58,237,0.75), rgba(13,78,254,0.75))',
                                }}
                              />
                            </Box>
                          </Box>
                          <ServiceCount isChild>{sub.count}</ServiceCount>
                        </ServiceGroupRow>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            )}
          </>
        ) : (
          <AnalyticsEmptyState sx={{ py: 3 }}>
            <MailOutlineIcon sx={{ fontSize: 36, opacity: 0.35 }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              No leads in this period yet
            </Typography>
          </AnalyticsEmptyState>
        )}
      </ServiceBreakdownCard>
    </AnalyticsSection>
  );
}

/** Local calendar date for the analytics date inputs (YYYY-MM-DD). */
function formatAnalyticsDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Same calendar day two months earlier. */
function twoMonthsBefore(date) {
  return new Date(date.getFullYear(), date.getMonth() - 2, date.getDate());
}

/* ─────────────────────────────────────────────
   Main component
   ───────────────────────────────────────────── */
function BusinessDashboard({ window }) {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [formState, setFormState] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [viewMode, setViewMode] = useState(true);
  const [originalState, setOriginalState] = useState({});
  const [photoError, setPhotoError] = useState('');
  const [logoCropOpen, setLogoCropOpen] = useState(false);
  const [logoCropSrc, setLogoCropSrc] = useState(null);
  const [logoCropUploadBase, setLogoCropUploadBase] = useState('business-logo');
  const [logoCropSubmitting, setLogoCropSubmitting] = useState(false);
  const [endSubDialogOpen, setEndSubDialogOpen] = useState(false);
  const [endSubSubmitting, setEndSubSubmitting] = useState(false);
  const [endSubDialogError, setEndSubDialogError] = useState(null);
  const [endSubSuccess, setEndSubSuccess] = useState(false);
  const [emailPopoverAnchor, setEmailPopoverAnchor] = useState(null);
  const [phonePopoverAnchor, setPhonePopoverAnchor] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [analyticsFrom, setAnalyticsFrom] = useState(() => formatAnalyticsDate(twoMonthsBefore(new Date())));
  const [analyticsTo, setAnalyticsTo] = useState(() => formatAnalyticsDate(new Date()));
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    if (user?.Role !== 'business_user' && user?.Role !== 'admin_user') {
      navigate('/');
      return;
    }

    let isCancelled = false;

    const fetchData = async () => {
      try {
        const userResponse = await accountService.getAccount();
        if (isCancelled) return;
        setUserData(userResponse);

        if (userResponse.BusinessId) {
          const businessResponse =
            await businessService.getBusinessForOwnerDashboard(
              userResponse.BusinessId,
            );
          if (!isCancelled) {
            setBusinessData(businessResponse);
            const formSnapshot = withFormattedBusinessPhone(businessResponse);
            setFormState(formSnapshot);
            setOriginalState(formSnapshot);
          }
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Failed to load business dashboard data:', err);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isCancelled = true;
    };
  }, [user, navigate]);

  useEffect(() => {
    if (!userData?.BusinessId) return;
    let isCancelled = false;
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    getBusinessAnalytics(userData.BusinessId, analyticsFrom, analyticsTo)
      .then((data) => {
        if (!isCancelled) {
          setAnalytics(data);
        }
      })
      .catch((err) => {
        if (!isCancelled) {
          setAnalyticsError(err?.message || 'Unable to load analytics');
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setAnalyticsLoading(false);
        }
      });
    return () => {
      isCancelled = true;
    };
  }, [userData?.BusinessId, analyticsFrom, analyticsTo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (
      ['AddressLine1', 'AddressLine2', 'City', 'State', 'ZipCode'].includes(name)
    ) {
      setFormState((prev) => ({
        ...prev,
        BusinessAddress: {
          ...prev.BusinessAddress,
          [name]: value,
        },
      }));
    } else if (name === 'BusinessPhoneNumber') {
      setFormState((prev) => ({
        ...prev,
        [name]: formatPhoneInputMask(value),
      }));
    } else {
      setFormState((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFormFieldBlur = (e) => {
    const { name, value } = e.target;
    const normalizedFields = [
      'AddressLine1',
      'AddressLine2',
      'City',
      'State',
      'ZipCode',
      'BusinessEmail',
      'BusinessWebsiteUrl',
    ];
    if (!normalizedFields.includes(name)) {
      return;
    }

    const normalized = normalizeBusinessFormFieldValue(name, value);
    if (normalized === value) return;

    if (
      ['AddressLine1', 'AddressLine2', 'City', 'State', 'ZipCode'].includes(name)
    ) {
      setFormState((prev) => ({
        ...prev,
        BusinessAddress: {
          ...prev.BusinessAddress,
          [name]: normalized,
        },
      }));
      return;
    }

    setFormState((prev) => ({ ...prev, [name]: normalized }));
  };

  const PublishChip = ({ published }) => (
    <Tooltip
      title={
        published
          ? 'Your storefront is live and visible to customers.'
          : 'Your storefront is currently offline and hidden from customers.'
      }
    >
      <Chip
        label={published ? 'Online' : 'Offline'}
        size="medium"
        icon={published ? <CheckCircleIcon /> : <CancelIcon />}
        sx={{
          mt: 1,
          ml: 2,
          fontWeight: 600,
          fontSize: '1rem',
          height: '36px',
          padding: '0 14px',
          borderRadius: '8px',
          bgcolor: published ? '#01EBB2' : 'red',
          color: '#FFFFFF',
          '& .MuiChip-icon': {
            color: '#FFFFFF',
          },
        }}
      />
    </Tooltip>
  );

  const handleHideAddressChange = (e) => {
    const hideFromPublic = e.target.checked;
    setFormState((prev) => ({
      ...prev,
      BusinessAddress: {
        ...prev.BusinessAddress,
        ShowAddressToPublic: !hideFromPublic,
      },
    }));
  };

  const handleSave = async () => {
    const errors = {};
    const addr = formState.BusinessAddress || {};

    if (
      !formState.BusinessEmail ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.BusinessEmail)
    ) {
      errors.BusinessEmail = 'Valid email is required';
    }
    const businessPhoneDigits = phoneDigitsForApi(formState.BusinessPhoneNumber);
    if (!businessPhoneDigits || businessPhoneDigits.length !== 10) {
      errors.BusinessPhoneNumber = 'Enter a 10-digit phone number';
    }
    if (!addr.AddressLine1) errors.AddressLine1 = 'Required';
    if (!addr.City) errors.City = 'Required';
    if (!addr.State) errors.State = 'Required';
    if (!addr.ZipCode) errors.ZipCode = 'Required';

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const normalizedAddress = normalizeBusinessAddress({
      ...addr,
      ShowAddressToPublic: addr.ShowAddressToPublic !== false,
    });

    try {
      setIsLoading(true);
      setSaveError(null);

      const updatePayload = {
        BusinessAbout: formState.BusinessAbout || '',
        BusinessEmail: normalizeBusinessEmail(formState.BusinessEmail),
        BusinessPhoneNumber: phoneDigitsForApi(formState.BusinessPhoneNumber),
        BusinessWebsiteUrl: normalizeBusinessWebsiteUrl(
          formState.BusinessWebsiteUrl || '',
        ),
        BusinessAddress: {
          AddressLine1: normalizedAddress.AddressLine1,
          AddressLine2: normalizedAddress.AddressLine2 || '',
          City: normalizedAddress.City,
          State: normalizedAddress.State,
          ZipCode: normalizedAddress.ZipCode,
          ShowAddressToPublic: normalizedAddress.ShowAddressToPublic !== false,
        },
      };

      await businessService.editBusiness(businessData.BusinessId, updatePayload);
      setFormState((prev) => ({
        ...prev,
        BusinessEmail: updatePayload.BusinessEmail,
        BusinessWebsiteUrl: updatePayload.BusinessWebsiteUrl,
        BusinessAddress: updatePayload.BusinessAddress,
      }));
      setOriginalState((prev) => ({
        ...prev,
        BusinessEmail: updatePayload.BusinessEmail,
        BusinessWebsiteUrl: updatePayload.BusinessWebsiteUrl,
        BusinessAddress: updatePayload.BusinessAddress,
      }));
      setSaveSuccess(true);
      setViewMode(true);
    } catch (err) {
      setSaveError('Failed to save changes. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const releaseLogoCropBlobUrl = () => {
    setLogoCropSrc((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const closeLogoCropModal = () => {
    if (logoCropSubmitting) return;
    setLogoCropOpen(false);
    releaseLogoCropBlobUrl();
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (e.target) {
      e.target.value = '';
    }
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('File size must be less than 5MB');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setPhotoError('File must be an image');
      return;
    }
    setPhotoError('');

    releaseLogoCropBlobUrl();
    const objectUrl = URL.createObjectURL(file);
    setLogoCropUploadBase(logoUploadBaseFromFileName(file.name));
    setLogoCropSrc(objectUrl);
    setLogoCropOpen(true);
  };

  const handleAdjustLogoFraming = async () => {
    if (!businessData?.BusinessPhotoUrl) return;
    setPhotoError('');
    releaseLogoCropBlobUrl();
    try {
      const blobUrl = await fetchImageAsBlobUrl(businessData.BusinessPhotoUrl);
      setLogoCropUploadBase(logoUploadBaseFromPhotoUrl(businessData.BusinessPhotoUrl));
      setLogoCropSrc(blobUrl);
      setLogoCropOpen(true);
    } catch (err) {
      console.error('Failed to load logo for cropping:', err);
      setPhotoError(
        'Could not load your current logo for editing. Upload a new image to adjust framing.',
      );
    }
  };

  const handleLogoCropConfirm = async (blob) => {
    if (!businessData?.BusinessId || !blob) return;
    try {
      setLogoCropSubmitting(true);
      setPhotoError('');
      const rawBase = logoCropUploadBase || 'business-logo';
      const safeBase = rawBase.replace(/[^a-zA-Z0-9-_]/g, '-').slice(0, 72);
      const fileName = `${safeBase}-logo.png`;
      const file = new File([blob], fileName, { type: 'image/png' });

      const photoUrl = await businessService.uploadBusinessImage(file, fileName);
      const updatePayload = { BusinessPhotoUrl: photoUrl };
      await businessService.editBusiness(businessData.BusinessId, updatePayload);

      const updatedBusinessData = { ...businessData, BusinessPhotoUrl: photoUrl };
      setBusinessData(updatedBusinessData);
      setFormState((prev) => ({
        ...updatedBusinessData,
        BusinessPhoneNumber: prev.BusinessPhoneNumber,
      }));

      setSaveSuccess(true);
      setLogoCropOpen(false);
      releaseLogoCropBlobUrl();
    } catch (error) {
      console.error('Error updating business photo:', error);
      setPhotoError('Failed to save logo. Please try again.');
    } finally {
      setLogoCropSubmitting(false);
    }
  };

  const addr = formState.BusinessAddress || {};
  const smsPrefs = businessData?.Sms || formState?.Sms || {};
  const smsLeadNotificationsEnabled = Boolean(smsPrefs.LeadNotificationsEnabled);
  const smsUsesBusinessPhone =
    smsLeadNotificationsEnabled && !smsPrefs.LeadNotificationPhoneNumber;
  const inquiryInboxPopoverPaperSx = {
    borderRadius: '14px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
    mt: 1,
    border: '1px solid #e4e8f1',
  };

  if (!user || isLoading) {
    return (
      <DashboardContainer
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <NkySpinner />
      </DashboardContainer>
    );
  }

  const handleCancel = () => {
    setFormState(originalState);
    setFormErrors({});
    setViewMode(true);
  };

  const handleOpenEndSubscriptionDialog = () => {
    setEndSubDialogError(null);
    setEndSubDialogOpen(true);
  };

  const handleCloseEndSubscriptionDialog = () => {
    if (endSubSubmitting) return;
    setEndSubDialogOpen(false);
    setEndSubDialogError(null);
  };

  const handleConfirmEndSubscription = async () => {
    const requesterEmail = (user?.Email || userData?.Email || '').trim();
    if (!businessData?.BusinessId || !requesterEmail) {
      setEndSubDialogError(
        'Missing business or account email. Sign in again or contact support.',
      );
      return;
    }
    setEndSubSubmitting(true);
    setEndSubDialogError(null);
    try {
      await messagingService.requestEndSubscription({
        businessId: businessData.BusinessId,
        requesterEmail,
      });
      setEndSubDialogOpen(false);
      setEndSubSuccess(true);
    } catch (err) {
      console.error('End subscription request failed:', err);
      setEndSubDialogError(
        'Could not send your request. Please try again or email support directly.',
      );
    } finally {
      setEndSubSubmitting(false);
    }
  };

  const demoWindow = window ? window() : undefined;

  return (
    <AppProvider
      branding={{ title: businessData?.BusinessName || '' }}
      theme={appTheme}
      window={demoWindow}
    >
      <DashboardContainer>
        <SidebarContainer>
          <DashboardNavigationBar />
        </SidebarContainer>

        <ContentWrapper>
          {!businessData?.PartnershipAgreementAcceptedAt && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
              Sign the{' '}
              <Link
                to="/dashboard/partnership-agreement"
                style={{ color: '#0d4efe', fontWeight: 700 }}
              >
                Business Partnership Agreement
              </Link>{' '}
              before your storefront can be published.
            </Alert>
          )}

          {!businessData?.BusinessPublished && <StorefrontOfflineAlert />}

          {/* ── Header ── */}
          <BusinessHeaderBox>
            <Box>
              {businessData?.BusinessName && (
                <BusinessHeading>{businessData.BusinessName}</BusinessHeading>
              )}
              <WelcomeText>
                Welcome back, {userData?.FirstName || user?.Email}
              </WelcomeText>
              {businessData?.BusinessPublished && (
                <StorefrontButton
                  onClick={() => navigateToStorefront(navigate, businessData)}
                  variant="contained"
                >
                  Visit My Storefront
                </StorefrontButton>
              )}
            </Box>

            <PhotoUploadContainer>
              <PhotoContainer>
                {businessData?.BusinessPhotoUrl ? (
                  <img
                    src={businessData.BusinessPhotoUrl}
                    alt={`${businessData.BusinessName}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <PhotoPlaceholder>
                    <CloudUploadIcon sx={{ fontSize: 50 }} />
                  </PhotoPlaceholder>
                )}
              </PhotoContainer>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="business-photo-upload"
                  type="file"
                  onChange={handlePhotoChange}
                />
                <label htmlFor="business-photo-upload">
                  <PhotoUploadButton
                    component="span"
                    variant="contained"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                  >
                    Upload new image
                  </PhotoUploadButton>
                </label>

                {businessData?.BusinessPhotoUrl ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CropRoundedIcon />}
                    onClick={handleAdjustLogoFraming}
                    disabled={logoCropSubmitting}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '10px',
                      color: '#fff',
                      borderColor: 'rgba(255,255,255,0.85)',
                      '&:hover': {
                        borderColor: '#fff',
                        backgroundColor: 'rgba(255,255,255,0.12)',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255,255,255,0.45)',
                        borderColor: 'rgba(255,255,255,0.35)',
                      },
                    }}
                  >
                    Edit logo
                  </Button>
                ) : null}
              </Box>

              {photoError && (
                <PhotoError variant="caption" color="error">
                  {photoError}
                </PhotoError>
              )}
            </PhotoUploadContainer>

            {businessData && (
              <PublishChip published={businessData.BusinessPublished} />
            )}
          </BusinessHeaderBox>

          {/* ═══ ANALYTICS – now ABOVE business info ═══ */}
          {userData?.BusinessId && (
            <AnalyticsPanel
              analytics={analytics}
              analyticsLoading={analyticsLoading}
              analyticsError={analyticsError}
              analyticsFrom={analyticsFrom}
              analyticsTo={analyticsTo}
              enrolledServices={businessData?.Services}
              onFromChange={setAnalyticsFrom}
              onToChange={setAnalyticsTo}
            />
          )}

          {/* ═══ BUSINESS INFO – now BELOW analytics ═══ */}
          <InfoPaper elevation={3}>
            {viewMode && (
              <EditButton
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setViewMode(false)}
              >
                Edit
              </EditButton>
            )}
            <BusinessInfoTitle>Business Details</BusinessInfoTitle>
            <FormNotice>
              This information appears on your storefront. Keeping it accurate
              helps NKY homeowners find and contact your business with
              confidence.
            </FormNotice>

            {viewMode ? (
              <>
                <InfoBlock>
                  <InfoBlockIconWrap bg="#f0f4ff" iconColor="#0d4efe">
                    <DescriptionOutlinedIcon />
                  </InfoBlockIconWrap>
                  <InfoBlockText>
                    <Label>About</Label>
                    <Value>{formState.BusinessAbout || 'N/A'}</Value>
                  </InfoBlockText>
                </InfoBlock>
                <InfoBlock>
                  <InfoBlockIconWrap bg="#eef8ff" iconColor="#0891b2">
                    <EmailOutlinedIcon />
                  </InfoBlockIconWrap>
                  <InfoBlockText>
                    <Label>Email</Label>
                    <Value>
                      {formState.BusinessEmail}
                      <InfoChip
                        onClick={(e) => setEmailPopoverAnchor(e.currentTarget)}
                      >
                        <NotificationsActiveOutlinedIcon />
                        Inquiry Inbox
                      </InfoChip>
                    </Value>
                  </InfoBlockText>
                  <Popover
                    open={Boolean(emailPopoverAnchor)}
                    anchorEl={emailPopoverAnchor}
                    onClose={() => setEmailPopoverAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                      paper: {
                        sx: inquiryInboxPopoverPaperSx,
                      },
                    }}
                  >
                    <PopoverContent>
                      <PopoverTitle>
                        <NotificationsActiveOutlinedIcon sx={{ color: '#0891b2', fontSize: '1.1rem' }} />
                        Inquiry Destination
                      </PopoverTitle>
                      <PopoverBody>
                        Customer inquiries submitted through your storefront will be
                        emailed to <strong>{formState.BusinessEmail}</strong>.
                        {smsUsesBusinessPhone ? (
                          <>
                            {' '}
                            With SMS notifications enabled, you also receive a text
                            at your business phone number.
                          </>
                        ) : smsLeadNotificationsEnabled ? (
                          <>
                            {' '}
                            With SMS notifications enabled, you also receive a text
                            at the phone number configured under SMS Notifications.
                          </>
                        ) : null}{' '}
                        To change where email inquiries go, update this email using
                        the edit button.
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                </InfoBlock>
                <InfoBlock>
                  <InfoBlockIconWrap bg="#e8faf5" iconColor="#01c99b">
                    <PhoneOutlinedIcon />
                  </InfoBlockIconWrap>
                  <InfoBlockText>
                    <Label>Phone Number</Label>
                    <Value>
                      {formatPhoneInputMask(formState.BusinessPhoneNumber || '') ||
                        '—'}
                      {smsUsesBusinessPhone ? (
                        <InfoChip
                          onClick={(e) => setPhonePopoverAnchor(e.currentTarget)}
                        >
                          <NotificationsActiveOutlinedIcon />
                          Inquiry Inbox
                        </InfoChip>
                      ) : null}
                    </Value>
                  </InfoBlockText>
                  <Popover
                    open={Boolean(phonePopoverAnchor)}
                    anchorEl={phonePopoverAnchor}
                    onClose={() => setPhonePopoverAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                      paper: {
                        sx: inquiryInboxPopoverPaperSx,
                      },
                    }}
                  >
                    <PopoverContent>
                      <PopoverTitle>
                        <NotificationsActiveOutlinedIcon sx={{ color: '#0891b2', fontSize: '1.1rem' }} />
                        Inquiry Destination
                      </PopoverTitle>
                      <PopoverBody>
                        Because SMS notifications are enabled for this business phone,
                        new service-request inquiries are also texted to{' '}
                        <strong>
                          {formatPhoneInputMask(formState.BusinessPhoneNumber || '') ||
                            'this number'}
                        </strong>
                        . Manage SMS preferences under SMS Notifications in the
                        dashboard.
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                </InfoBlock>
                <InfoBlock>
                  <InfoBlockIconWrap bg="#fef3e8" iconColor="#d97706">
                    <LanguageOutlinedIcon />
                  </InfoBlockIconWrap>
                  <InfoBlockText>
                    <Label>Website</Label>
                    <Value>{formState.BusinessWebsiteUrl || '—'}</Value>
                  </InfoBlockText>
                </InfoBlock>
                <InfoBlock>
                  <InfoBlockIconWrap bg="#fce8ee" iconColor="#dc2663">
                    <LocationOnOutlinedIcon />
                  </InfoBlockIconWrap>
                  <InfoBlockText>
                    <Label>Address</Label>
                    <Value>{`${addr.AddressLine1 || ''} ${addr.AddressLine2 || ''}, ${addr.City || ''}, ${addr.State || ''} ${addr.ZipCode || ''}`}</Value>
                    <Chip
                      size="small"
                      label={
                        addr.ShowAddressToPublic === false
                          ? 'Street address hidden on storefront'
                          : 'Street address visible on storefront'
                      }
                      sx={{
                        mt: 1,
                        alignSelf: 'flex-start',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: '26px',
                        bgcolor:
                          addr.ShowAddressToPublic === false
                            ? 'rgba(217, 119, 6, 0.12)'
                            : 'rgba(1, 201, 155, 0.14)',
                        color:
                          addr.ShowAddressToPublic === false ? '#d97706' : '#01a37d',
                      }}
                    />
                  </InfoBlockText>
                </InfoBlock>
              </>
            ) : (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <LabeledInputWrapper>
                      <LabelWithIcon>
                        About
                        <Tooltip title="Tell customers what your business does, what makes you unique, and the types of services you specialize in.">
                          <InfoOutlinedIcon />
                        </Tooltip>
                      </LabelWithIcon>
                      <StyledTextField
                        name="BusinessAbout"
                        value={formState.BusinessAbout || ''}
                        onChange={handleInputChange}
                        multiline
                      />
                    </LabeledInputWrapper>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <LabeledInputWrapper>
                      <LabelWithIcon>
                        Email
                        <Tooltip title="Public-facing email for customers to get in touch with your business.">
                          <InfoOutlinedIcon />
                        </Tooltip>
                      </LabelWithIcon>
                      <StyledTextField
                        name="BusinessEmail"
                        value={formState.BusinessEmail || ''}
                        onChange={handleInputChange}
                        onBlur={handleFormFieldBlur}
                        error={!!formErrors.BusinessEmail}
                        helperText={formErrors.BusinessEmail}
                      />
                    </LabeledInputWrapper>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <LabeledInputWrapper>
                      <LabelWithIcon>
                        Phone Number
                        <Tooltip title="Shown on your public page so customers can call you with questions or service requests.">
                          <InfoOutlinedIcon />
                        </Tooltip>
                      </LabelWithIcon>
                      <StyledTextField
                        name="BusinessPhoneNumber"
                        type="tel"
                        value={formState.BusinessPhoneNumber || ''}
                        onChange={handleInputChange}
                        placeholder="555-123-4567"
                        inputProps={{
                          inputMode: 'numeric',
                          autoComplete: 'tel',
                        }}
                        error={!!formErrors.BusinessPhoneNumber}
                        helperText={formErrors.BusinessPhoneNumber}
                      />
                    </LabeledInputWrapper>
                  </Grid>

                  <Grid item xs={12}>
                    <LabeledInputWrapper>
                      <LabelWithIcon>
                        Website URL
                        <Tooltip title="Link to your business website to build trust and give customers another way to learn about or connect with you.">
                          <InfoOutlinedIcon />
                        </Tooltip>
                      </LabelWithIcon>
                      <StyledTextField
                        name="BusinessWebsiteUrl"
                        value={formState.BusinessWebsiteUrl || ''}
                        onChange={handleInputChange}
                        onBlur={handleFormFieldBlur}
                      />
                    </LabeledInputWrapper>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <LabeledInputWrapper>
                      <LabelWithIcon>
                        Address Line 1
                        <Tooltip title="Street address where your business is located.">
                          <InfoOutlinedIcon />
                        </Tooltip>
                      </LabelWithIcon>
                      <StyledTextField
                        name="AddressLine1"
                        value={addr.AddressLine1 || ''}
                        onChange={handleInputChange}
                        onBlur={handleFormFieldBlur}
                        error={!!formErrors.AddressLine1}
                        helperText={formErrors.AddressLine1}
                      />
                    </LabeledInputWrapper>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <LabeledInputWrapper>
                      <LabelWithIcon>
                        Address Line 2
                        <Tooltip title="Suite, unit, or apartment (optional).">
                          <InfoOutlinedIcon />
                        </Tooltip>
                      </LabelWithIcon>
                      <StyledTextField
                        name="AddressLine2"
                        value={addr.AddressLine2 || ''}
                        onChange={handleInputChange}
                        onBlur={handleFormFieldBlur}
                      />
                    </LabeledInputWrapper>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <LabeledInputWrapper>
                      <LabelWithIcon>
                        City
                        <Tooltip title="Select the city where your business is located.">
                          <InfoOutlinedIcon />
                        </Tooltip>
                      </LabelWithIcon>
                      <StyledTextField
                        name="City"
                        value={addr.City || ''}
                        onChange={handleInputChange}
                        onBlur={handleFormFieldBlur}
                        error={!!formErrors.City}
                        helperText={formErrors.City}
                      />
                    </LabeledInputWrapper>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <LabeledInputWrapper>
                      <LabelWithIcon>
                        State
                        <Tooltip title="Select the state where your business is located.">
                          <InfoOutlinedIcon />
                        </Tooltip>
                      </LabelWithIcon>
                      <StyledTextField
                        select
                        name="State"
                        value={addr.State || ''}
                        onChange={handleInputChange}
                        onBlur={handleFormFieldBlur}
                        error={!!formErrors.State}
                        helperText={formErrors.State}
                      >
                        {SERVICE_AREA_STATES.map((state) => (
                          <MenuItem key={state.code} value={state.code}>
                            {state.name}
                          </MenuItem>
                        ))}
                      </StyledTextField>
                    </LabeledInputWrapper>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <LabeledInputWrapper>
                      <LabelWithIcon>
                        Zip Code
                        <Tooltip title="Select the zip code for where your business is located.">
                          <InfoOutlinedIcon />
                        </Tooltip>
                      </LabelWithIcon>
                      <StyledTextField
                        name="ZipCode"
                        value={addr.ZipCode || ''}
                        onChange={handleInputChange}
                        onBlur={handleFormFieldBlur}
                        error={!!formErrors.ZipCode}
                        helperText={formErrors.ZipCode}
                      />
                    </LabeledInputWrapper>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={addr.ShowAddressToPublic === false}
                          onChange={handleHideAddressChange}
                          name="HideAddressFromPublic"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: '#1a1a2e' }}>
                          Hide street address from public profile
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>

                <SaveButton variant="contained" onClick={handleSave}>
                  Save Changes
                </SaveButton>
                <CancelButton variant="outlined" onClick={handleCancel}>
                  Cancel
                </CancelButton>
              </>
            )}
          </InfoPaper>

          {businessData?.BusinessId ? (
            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: 3,
                borderRadius: '16px',
                border: '1px solid rgba(220, 38, 38, 0.18)',
                background:
                  'linear-gradient(135deg, rgba(254, 242, 242, 0.9) 0%, #fff 55%)',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '1.05rem',
                  color: '#1a1a2e',
                  mb: 0.5,
                }}
              >
                Subscription
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#5a6478', mb: 2, maxWidth: 560, lineHeight: 1.6 }}
              >
                To cancel your subscription, send a request to NKY Connect. The team will
                receive an email with your business name and follow up with you.
              </Typography>
              <Button
                variant="outlined"
                color="error"
                startIcon={<UnsubscribeOutlinedIcon />}
                onClick={handleOpenEndSubscriptionDialog}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
              >
                End subscription
              </Button>
            </Paper>
          ) : null}

          <Dialog
            open={endSubDialogOpen}
            onClose={() => {
              if (endSubSubmitting) return;
              handleCloseEndSubscriptionDialog();
            }}
            disableEscapeKeyDown={endSubSubmitting}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: { borderRadius: '16px', border: '1px solid rgba(13, 78, 254, 0.12)' },
            }}
          >
            <DialogTitle sx={{ fontWeight: 800, color: '#1a1a2e', pb: 1 }}>
              End subscription?
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ color: '#5a6478', lineHeight: 1.65, mb: 1 }}>
                We will email NKY Connect that{' '}
                <strong>{businessData?.BusinessName || 'your business'}</strong> has requested
                to end its subscription. Someone from the team will follow up.
              </Typography>
              {endSubDialogError ? (
                <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>
                  {endSubDialogError}
                </Alert>
              ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
              <Button
                onClick={handleCloseEndSubscriptionDialog}
                disabled={endSubSubmitting}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#5a6478',
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={endSubSubmitting}
                onClick={handleConfirmEndSubscription}
                startIcon={
                  endSubSubmitting ? (
                    <NkySpinner size="sm" onDark />
                  ) : null
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: '10px',
                  boxShadow: 'none',
                }}
              >
                {endSubSubmitting ? 'Sending…' : 'Send request'}
              </Button>
            </DialogActions>
          </Dialog>

          <NkySnackbarAlert
            open={!!saveSuccess}
            autoHideDuration={6000}
            onClose={() => setSaveSuccess(false)}
            severity="success"
            message="Changes saved successfully!"
          />

          <NkySnackbarAlert
            open={!!saveError}
            autoHideDuration={6000}
            onClose={() => setSaveError(null)}
            severity="error"
            message={saveError || ''}
          />

          <NkySnackbarAlert
            open={endSubSuccess}
            autoHideDuration={6000}
            onClose={() => setEndSubSuccess(false)}
            severity="success"
            message="Your subscription cancellation request was sent to NKY Connect."
          />
        </ContentWrapper>

        <BusinessLogoCropModal
          open={logoCropOpen}
          imageSrc={logoCropSrc || undefined}
          onClose={closeLogoCropModal}
          onCroppedConfirm={handleLogoCropConfirm}
          isSubmitting={logoCropSubmitting}
        />
      </DashboardContainer>
    </AppProvider>
  );
}

BusinessDashboard.propTypes = {
  window: PropTypes.func,
};

export default BusinessDashboard;